import type { GraphModel } from './graphModel';
import type { SimEdge, SimNode } from './simulation';

const CLASS_LABELS: Readonly<Record<string, string>> = {
  Kunde: 'Kunde', ProduktType: 'Produkt', ProduktVersion: 'Produktversion',
  KomponenteType: 'Komponente', KomponenteVersion: 'Komponentenversion',
  ProduktVersionKomponente: 'Stücklisteneintrag', ProduktInstanz: 'Produktinstanz', CVE: 'CVE',
  VulnerabilityEvent: 'Bewertung', Benachrichtigung: 'Benachrichtigung',
};

export function classLabel(klass: string): string {
  return CLASS_LABELS[klass] ?? klass;
}

// Read a reference from either end without inventing a purchase or an impact.
const INVERSE_LABELS: Readonly<Record<string, string>> = {
  'geliefert an': 'besitzt', 'ist ein': 'hat Instanz',
  'Version von': 'hat Version', 'Vorgänger': 'hat Nachfolger',
  'ausgeliefert mit': 'Auslieferungsversion von', 'läuft mit': 'wird eingesetzt in',
  'enthält': 'ist enthalten in', 'betroffen von': 'betrifft',
  'basiert auf': 'Basis von', 'Kundenversion von': 'hat Kundenversion',
  'bewertet': 'hat Bewertung', 'zu CVE': 'hat Bewertung',
  'aus Bewertung': 'hat Benachrichtigung', 'betrifft': 'hat Benachrichtigung',
  'wegen': 'Anlass für Benachrichtigung', 'Stückliste': 'hat Stücklisteneintrag',
};

export function relationLabel(edge: SimEdge, fromId: string): string {
  return edge.source.id === fromId ? edge.label : INVERSE_LABELS[edge.label] ?? `← ${edge.label}`;
}

export interface RelationGroup {
  key: string;
  label: string;
  direction: 'out' | 'in';
  klass: string;
  nodes: SimNode[];
}

export function relationGroups(model: GraphModel, nodeId: string): RelationGroup[] {
  const groups = new Map<string, RelationGroup>();
  for (const edge of model.incident.get(nodeId) ?? []) {
    const outgoing = edge.source.id === nodeId;
    const other = outgoing ? edge.target : edge.source;
    const direction = outgoing ? 'out' : 'in';
    const key = JSON.stringify([direction, edge.label, other.klass]);
    const group = groups.get(key) ?? {
      key, label: relationLabel(edge, nodeId), direction, klass: other.klass, nodes: [],
    };
    if (!group.nodes.some((node) => node.id === other.id)) group.nodes.push(other);
    groups.set(key, group);
  }
  return [...groups.values()].map((group) => ({
    ...group, nodes: group.nodes.sort((a, b) => a.label.localeCompare(b.label, 'de', { numeric: true })),
  }));
}

/*
 * Record sheet ("Datenblatt"): every connection of one record at once, named in
 * domain terms instead of edge labels.
 *
 * Edges point from the card that holds the reference to the card it names, so
 * an OUTGOING edge is a single value of this record (its Kunde, its Produkt …)
 * and becomes a field; INCOMING edges are the records that point here and
 * become lists. The collapsed bill of materials ("enthält") is the one N:M
 * edge and always forms a list.
 */

/** Field name per outgoing edge label; anything else is named after the target class. */
const FIELD_LABELS: Readonly<Record<string, string>> = {
  'geliefert an': 'Kunde', 'ist ein': 'Produkt', 'ausgeliefert mit': 'Ausgelieferte Version',
  'läuft mit': 'Aktuelle Version', 'Vorgänger': 'Vorgängerversion', 'betroffen von': 'CVE',
  'basiert auf': 'Basisversion', 'Kundenversion von': 'Produktinstanz',
  'bewertet': 'Komponentenversion', 'zu CVE': 'CVE', 'aus Bewertung': 'Bewertung',
  'betrifft': 'Produktinstanz', 'wegen': 'Komponentenversion',
};

/** List title per incoming edge label; anything else is the source class's plural. */
const LIST_LABELS: Readonly<Record<string, string>> = {
  'geliefert an': 'Produktinstanzen', 'ist ein': 'Produktinstanzen', 'Version von': 'Versionen',
  'Vorgänger': 'Nachfolgeversionen', 'ausgeliefert mit': 'Ausgeliefert an', 'läuft mit': 'Im Einsatz bei',
  'betroffen von': 'Betroffene Komponentenversionen', 'basiert auf': 'Kundenversionen',
  'Kundenversion von': 'Kundenversionen', 'bewertet': 'Bewertungen', 'zu CVE': 'Bewertungen',
  'aus Bewertung': 'Benachrichtigungen', 'betrifft': 'Benachrichtigungen', 'wegen': 'Benachrichtigungen',
  'enthält': 'Enthalten in',
};

/** Outgoing edges that are N:M and therefore a list, with their title. */
const OUTGOING_LISTS: Readonly<Record<string, string>> = { 'enthält': 'Stückliste' };

const FIELD_ORDER = [
  'Kunde', 'Produkt', 'Komponente', 'Produktinstanz', 'Ausgelieferte Version', 'Aktuelle Version',
  'Vorgängerversion', 'Basisversion', 'Komponentenversion', 'CVE', 'Bewertung',
];

export interface SheetField { label: string; node: SimNode }
export interface SheetList { label: string; nodes: SimNode[] }
export interface RecordSheet { fields: SheetField[]; lists: SheetList[] }

const byLabel = (a: SimNode, b: SimNode): number => a.label.localeCompare(b.label, 'de', { numeric: true });

/**
 * The record sheet of `nodeId`. `excludeId` (the step the user came from) is
 * left out: it is already on screen in the path. `plurals` names the lists the
 * label table does not cover, keyed by class.
 */
export function recordSheet(
  model: GraphModel,
  nodeId: string,
  plurals: Readonly<Record<string, string>> = {},
  excludeId: string | null = null,
): RecordSheet {
  const fields = new Map<string, SimNode[]>();
  const lists = new Map<string, SimNode[]>();
  const add = (map: Map<string, SimNode[]>, label: string, node: SimNode): void => {
    const bucket = map.get(label) ?? [];
    if (!bucket.some((entry) => entry.id === node.id)) bucket.push(node);
    map.set(label, bucket);
  };
  for (const edge of model.incident.get(nodeId) ?? []) {
    const outgoing = edge.source.id === nodeId;
    const other = outgoing ? edge.target : edge.source;
    if (other.id === excludeId || other.id === nodeId) continue;
    if (outgoing && OUTGOING_LISTS[edge.label]) add(lists, OUTGOING_LISTS[edge.label]!, other);
    else if (outgoing) add(fields, FIELD_LABELS[edge.label] ?? classLabel(other.klass), other);
    else add(lists, LIST_LABELS[edge.label] ?? plurals[other.klass] ?? classLabel(other.klass), other);
  }
  // A reference that unexpectedly names several records reads as a list.
  for (const [label, nodes] of [...fields]) {
    if (nodes.length > 1) { fields.delete(label); for (const node of nodes) add(lists, label, node); }
  }
  const rank = (label: string): number => {
    const index = FIELD_ORDER.indexOf(label);
    return index < 0 ? FIELD_ORDER.length : index;
  };
  return {
    fields: [...fields].map(([label, nodes]) => ({ label, node: nodes[0]! }))
      .sort((a, b) => rank(a.label) - rank(b.label) || a.label.localeCompare(b.label, 'de')),
    lists: [...lists].map(([label, nodes]) => ({ label, nodes: nodes.sort(byLabel) }))
      .sort((a, b) => a.label.localeCompare(b.label, 'de')),
  };
}

/*
 * Vulnerabilities derived from the bill of materials. The graph's own
 * CVE → Produkt link (CveProduktType) is only ever added by the KEV scan and
 * never pruned, and it does not say which version; walking the Stückliste is
 * always current and names the version and component a CVE comes in through:
 *
 *   ProduktType ←"Version von"— ProduktVersion —"enthält"→ KomponenteVersion —"betroffen von"→ CVE
 */

export interface DerivedVulnerability { cve: SimNode; components: SimNode[]; versions: SimNode[] }
export interface DerivedProduct { product: SimNode; versions: SimNode[] }

function linked(model: GraphModel, id: string, label: string, direction: 'out' | 'in', klass: string): SimNode[] {
  return (model.incident.get(id) ?? []).flatMap((edge) => {
    if (edge.label !== label) return [];
    const from = direction === 'out' ? edge.source : edge.target;
    const to = direction === 'out' ? edge.target : edge.source;
    return from.id === id && to.klass === klass ? [to] : [];
  });
}

const STATE_RANK: Readonly<Record<string, number>> = { danger: 0, warn: 1, ok: 2 };
const byRisk = (a: SimNode, b: SimNode): number =>
  (STATE_RANK[a.state] ?? 3) - (STATE_RANK[b.state] ?? 3) || byLabel(a, b);

function addUnique(list: SimNode[], node: SimNode): void {
  if (!list.some((entry) => entry.id === node.id)) list.push(node);
}

/** CVEs reaching a Produkt or one ProduktVersion through its components; [] for any other class. */
export function bomVulnerabilities(model: GraphModel, nodeId: string): DerivedVulnerability[] {
  const node = model.byId.get(nodeId);
  if (!node) return [];
  const versions = node.klass === 'ProduktType' ? linked(model, nodeId, 'Version von', 'in', 'ProduktVersion')
    : node.klass === 'ProduktVersion' ? [node] : [];
  const byCve = new Map<string, DerivedVulnerability>();
  for (const version of versions) {
    for (const component of linked(model, version.id, 'enthält', 'out', 'KomponenteVersion')) {
      for (const cve of linked(model, component.id, 'betroffen von', 'out', 'CVE')) {
        const entry = byCve.get(cve.id) ?? { cve, components: [], versions: [] };
        addUnique(entry.components, component);
        addUnique(entry.versions, version);
        byCve.set(cve.id, entry);
      }
    }
  }
  return [...byCve.values()]
    .map((entry) => ({ ...entry, components: entry.components.sort(byLabel), versions: entry.versions.sort(byLabel) }))
    .sort((a, b) => byRisk(a.cve, b.cve));
}

/** The reverse walk: Produkte (with the affected versions) a CVE reaches; [] for any other class. */
export function bomAffectedProducts(model: GraphModel, nodeId: string): DerivedProduct[] {
  const node = model.byId.get(nodeId);
  if (!node || node.klass !== 'CVE') return [];
  const byProduct = new Map<string, DerivedProduct>();
  for (const component of linked(model, nodeId, 'betroffen von', 'in', 'KomponenteVersion')) {
    for (const version of linked(model, component.id, 'enthält', 'in', 'ProduktVersion')) {
      for (const product of linked(model, version.id, 'Version von', 'out', 'ProduktType')) {
        const entry = byProduct.get(product.id) ?? { product, versions: [] };
        addUnique(entry.versions, version);
        byProduct.set(product.id, entry);
      }
    }
  }
  return [...byProduct.values()]
    .map((entry) => ({ ...entry, versions: entry.versions.sort(byLabel) }))
    .sort((a, b) => byLabel(a.product, b.product));
}

/*
 * Customer summary: the questions one asks about a Kunde, answered two hops
 * away without clicking through each Produktinstanz.
 *
 * An instance runs the Stückliste of its current version ("läuft mit"). A
 * deviating instance runs a custom version of its own ("Kundenversion von",
 * DEV-107), so the Stückliste is always the whole truth.
 */

export interface CustomerSummary {
  instances: number;
  /** Instances whose effective configuration holds a KEV-affected component version. */
  kevAffected: number;
  notificationsOpen: number;
  notificationsSent: number;
  /** Instances running a custom version of their own. */
  customVersions: number;
}

/** The component versions an instance actually runs. */
export function effectiveComponents(model: GraphModel, instanceId: string): SimNode[] {
  const version = linked(model, instanceId, 'läuft mit', 'out', 'ProduktVersion')[0];
  return version ? linked(model, version.id, 'enthält', 'out', 'KomponenteVersion') : [];
}

/** Null for anything but a Kunde. */
export function customerSummary(model: GraphModel, nodeId: string): CustomerSummary | null {
  if (model.byId.get(nodeId)?.klass !== 'Kunde') return null;
  const instances = linked(model, nodeId, 'geliefert an', 'in', 'ProduktInstanz');
  const summary: CustomerSummary = { instances: instances.length, kevAffected: 0, notificationsOpen: 0, notificationsSent: 0, customVersions: 0 };
  for (const instance of instances) {
    if (effectiveComponents(model, instance.id).some((component) => component.state === 'danger')) summary.kevAffected += 1;
    for (const notice of linked(model, instance.id, 'betrifft', 'in', 'Benachrichtigung')) {
      if (notice.state === 'ok') summary.notificationsSent += 1; else summary.notificationsOpen += 1;
    }
    const version = linked(model, instance.id, 'läuft mit', 'out', 'ProduktVersion')[0];
    if (version && linked(model, version.id, 'Kundenversion von', 'out', 'ProduktInstanz').length > 0) {
      summary.customVersions += 1;
    }
  }
  return summary;
}

export function matchesNode(node: SimNode, term: string): boolean {
  const query = term.trim().toLocaleLowerCase('de');
  return `${node.label} ${node.detail} ${node.context ?? ''} ${classLabel(node.klass)} ${node.cardId}`.toLocaleLowerCase('de').includes(query);
}

export function initialRoot(model: GraphModel): string | null {
  return model.nodes.find((node) => node.klass === 'Kunde' && model.neighbours.has(node.id))?.id
    ?? model.nodes.find((node) => model.neighbours.has(node.id))?.id
    ?? model.nodes[0]?.id ?? null;
}
